import type { DocsSection } from "@/presentation/data/docs/docs-content.types";

export const environmentsSection: DocsSection = {
  label: "Environments",
  pages: [
    {
      slug: "environments-overview",
      title: "Overview",
      description: "dev vs staging — accounts, region, hostnames.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Two AWS environments run on ECS Fargate in region ",
            { code: "il-central-1" },
            ", each with ERPNext v16.53.0 + UnoPIM behind one ",
            { code: "*.yango.moveodevelop.com" },
            " wildcard cert.",
          ],
        },
        {
          kind: "table",
          headers: ["Env", "AWS account", "ERPNext host", "PIM host"],
          rows: [
            [
              { strong: "dev" },
              { code: "225726003331" },
              { code: "erp-dev.yango.moveodevelop.com" },
              { code: "pim-dev.yango.moveodevelop.com" },
            ],
            [
              { strong: "staging" },
              { code: "447633181771" },
              { code: "erp-staging.yango.moveodevelop.com" },
              { code: "pim-staging.yango.moveodevelop.com" },
            ],
          ],
        },
        {
          kind: "callout",
          tone: "warn",
          title: "Reachability",
          text: "Hosts are reachable only from the operator IP allowlist (fail-closed) or from in-VPC. A request from outside the allowlist is rejected, not served.",
        },
      ],
    },
    {
      slug: "access-and-secrets",
      title: "Access & secrets",
      description: "IP allowlist and the Secrets Manager layout.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: "Public access is fail-closed behind an operator IP allowlist. All service credentials live in AWS Secrets Manager, namespaced per environment.",
        },
        {
          kind: "heading",
          id: "secrets-layout",
          text: "Secrets Manager layout",
        },
        {
          kind: "table",
          headers: ["Secret", "Contents"],
          rows: [
            [
              { code: "yango/<env>/unopim-oauth" },
              { code: "{ client_id, client_secret, username, password }" },
            ],
            [
              { code: "yango/<env>/erpnext-admin-api" },
              [
                { code: "{ api_key, api_secret }" },
                " — ADMIN/System-Manager token (Administrator). Ops & provisioning scripts; can create Users.",
              ],
            ],
            [
              { code: "yango/<env>/erpnext-buyer-api" },
              [
                { code: "{ api_key, api_secret }" },
                " — SCOPED least-privilege token (svc-buyer-api). What apps/api runs as.",
              ],
            ],
            [{ code: "yango/<env>/erpnext-admin" }, "ERPNext desk admin password."],
          ],
        },
        {
          kind: "callout",
          tone: "success",
          title: "erpnext-admin-api is live on both envs",
          text: [
            "The ",
            { code: "erpnext-admin-api" },
            " secret now holds real ",
            { code: "api_key" },
            " / ",
            { code: "api_secret" },
            " on both dev and staging — the ERP REST API is usable on each. The scoped ",
            { code: "erpnext-buyer-api" },
            " token (apps/api runtime) is minted separately by provision-buyer-api-user.py.",
          ],
        },
        {
          kind: "heading",
          id: "desk-admins",
          text: "Desk admins",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            [
              { strong: "ERPNext" },
              " — ",
              { code: "Administrator" },
              " (or ",
              { code: "yango-admin" },
              " on staging); password in ",
              { code: "yango/<env>/erpnext-admin" },
              ".",
            ],
            [
              { strong: "UnoPIM" },
              " — ",
              { code: "yango-admin" },
              " on staging; ",
              { code: "admin/admin" },
              " on dev (CE fixtures default).",
            ],
          ],
        },
      ],
    },
    {
      slug: "observability",
      title: "Observability",
      description: "Alarms, alert routing, logs, and the cost posture.",
      stub: false,
      blocks: [
        {
          kind: "paragraph",
          text: [
            "Each env has an ",
            { code: "observability" },
            " module: CloudWatch alarms fan into one per-env SNS topic, plus VPC flow logs and ALB access logs. Alarm names are prefixed ",
            { code: "yango-<env>-" },
            ".",
          ],
        },
        {
          kind: "heading",
          id: "alerts-pipeline",
          text: "Alert routing",
        },
        {
          kind: "paragraph",
          text: [
            "Alarms publish to the SNS topic ",
            { code: "alerts" },
            " (encrypted with the AWS-managed key ",
            { code: "alias/aws/sns" },
            "). The ",
            { code: "alert_email" },
            " tfvar is ",
            { strong: "inert until populated" },
            ": empty ⇒ the topic exists but has no subscription; set it ⇒ an email subscription is created (confirm the SNS opt-in email once).",
          ],
        },
        {
          kind: "heading",
          id: "what-is-alarmed",
          text: "What's alarmed",
        },
        {
          kind: "table",
          headers: ["Layer", "Alarms"],
          rows: [
            [
              { strong: "RDS" },
              "CPU high, free-storage low, connections high — per DB instance (ERPNext MariaDB + UnoPIM Postgres).",
            ],
            [
              { strong: "ALB" },
              [
                { code: "HTTPCode_ELB_5XX_Count" },
                ", unhealthy-host count, and ",
                { code: "TargetResponseTime" },
                " latency.",
              ],
            ],
            [
              { strong: "ECS" },
              "Per-service CPU + memory (default AWS/ECS namespace — Container Insights is off).",
            ],
            [
              { strong: "Served 5xx" },
              [
                "Log metric filters count access-log lines with ",
                { code: "status >= 500" },
                " (per service) — symptom-based, not nginx ",
                { code: "[error]" },
                " noise (BF-193).",
              ],
            ],
          ],
        },
        {
          kind: "heading",
          id: "logs-retention",
          text: "Logs & retention",
        },
        {
          kind: "list",
          ordered: false,
          items: [
            [
              { strong: "VPC flow logs" },
              " → CloudWatch via a least-privilege delivery role. dev = ",
              { code: "REJECT" },
              " / 14d; staging = ",
              { code: "ALL" },
              " / 30d.",
            ],
            [
              { strong: "ALB access logs" },
              " → an env S3 bucket (SSE-S3, public-access blocked, lifecycle expiry). SSE-S3 not a CMK — ELB log delivery can't write to a CMK bucket (recorded deviation).",
            ],
            [
              { strong: "Product log groups" },
              " (ERPNext / UnoPIM / assessment) retain 30d on dev, 90d on staging.",
            ],
          ],
        },
        {
          kind: "callout",
          tone: "info",
          title: "Cost posture",
          text: "VPC flow logs are the one line that scales with traffic, so traffic_type + retention are per-env knobs and kept short. Container Insights stays off (service metrics come free from the default AWS/ECS namespace). The off-hours ECS scaler (BF-186) scales services down outside working hours and suppresses the resulting alarm noise so a deliberately scaled-down service never pages.",
        },
      ],
    },
  ],
};
